import {Helmet} from '@dr.pogodin/react-helmet';
import {openConfirmDialog, openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteTireAspectRatioMutation,
  usePostTireAspectRatioSetActiveMutation,
  usePostTireAspectRatioSetInactiveMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function TireInventoryAspectRatio() {
  const navigate = useNavigate();

  const [deleteTireAspectRatioApi] = useDeleteTireAspectRatioMutation();
  const [postTireAspectRatioSetActiveApi] = usePostTireAspectRatioSetActiveMutation();
  const [postTireAspectRatioSetInactiveApi] = usePostTireAspectRatioSetInactiveMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        navigate(composePath(settingsRoutes.tireAspectRatioDetail, {params: {id}}))
      )
      .with('setAsInactive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireAspectRatioSetInactiveApi({tireAspectRatioId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setAsActive', () =>
        openConfirmDialog({
          onConfirm: () =>
            postTireAspectRatioSetActiveApi({tireAspectRatioId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteTireAspectRatioApi({tireAspectRatioId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      );
  };

  return (
    <>
      <Helmet title={i18n.t('entity.tiresInventory.labels.tireAspectRatio')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.tiresInventory.labels.tireAspectRatio'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.tiresInventory.labels.newTireAspectRatio'),
              onClick: () => navigate(settingsRoutes.tireAspectRatioCreate),
            },
          ],
        }}
        data-testid={testIds.settings.tireAspectRatio('template')}
      >
        <DmsDataGrid
          gridCode="tire-aspect-ratio"
          actionCallback={actionCallback}
          data-testid={testIds.settings.tireAspectRatio('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
